import { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { boundaryCRUD } from "../../../actions/boundary";
import { selector } from "../../../api";

export const UploadBoundary = ({ setIndex, index, boundaryType, parentList }) => {
  const user = selector.User();
  const boundaryApi = process.env.REACT_APP_BOUNDARY_API;
  // Street/Road are linear -> their geometry column is a line, not a polygon.
  const isLine = boundaryType === "Street" || boundaryType === "Road";
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState("");
  const [previewData, setPreviewData] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [headers, setHeaders] = useState([]);
  const closeRef = useRef();
  const fileInputRef = useRef();

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error("Please select a CSV file", { autoClose: 5000 });
        return;
      }
      setFile(selectedFile);
      setFileName(selectedFile.name);
      parseCSV(selectedFile);
    }
  };

  const parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim() !== '');

      if (lines.length < 2) {
        toast.error("CSV file must have headers and at least one data row", { autoClose: 5000 });
        return;
      }

      // Parse headers
      const headerRow = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      setHeaders(headerRow);

      // Parse data rows (limit preview to first 10 rows)
      const dataRows = lines.slice(1, 11).map(line => {
        const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
        const row = {};
        headerRow.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        return row;
      });

      setPreviewData(dataRows);
      setShowPreview(true);
    };
    reader.readAsText(file);
  };

  const handleUpload = () => {
    if (!file) {
      toast.error("Please select a file to upload", { autoClose: 5000 });
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("user_id", user.id);
    formData.append("boundary_type", boundaryType);

    boundaryCRUD.uploadBoundary(formData)
      .then((response) => {
        const { created, errors } = response.data;
        if (created > 0) {
          toast.success(`Successfully uploaded ${created} ${boundaryType} records!`, { autoClose: 5000 });
        }
        if (errors && errors.length > 0) {
          errors.forEach(err => {
            toast.warning(`Row ${err.row}: ${err.message}`, { autoClose: 7000 });
          });
        }
        setIndex(index + 1);
        handleReset();
        closeRef.current.click();
      })
      .catch((error) => {
        const errorMessage = error.response?.data?.error || "Failed to upload boundaries";
        toast.error(errorMessage, { autoClose: 5000 });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleReset = () => {
    setFile(null);
    setFileName("");
    setPreviewData([]);
    setShowPreview(false);
    setHeaders([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const totalRows = previewData.length;
  const modalClass = `modal fade uploadBoundary${boundaryType}Modal`;

  return (
    <div className={modalClass} tabIndex="-1" aria-labelledby="uploadBoundaryModalLabel" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="uploadBoundaryModalLabel">
              <i className="bi bi-upload me-2"></i>
              Upload {boundaryType}
            </h5>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" ref={closeRef} onClick={handleReset}></button>
          </div>
          <div className="modal-body">
            {!showPreview ? (
              <>
                <div className="d-flex justify-content-end mb-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-soft-primary"
                    onClick={() => window.open(
                      `${boundaryApi}/boundary/upload/template?type=${encodeURIComponent(boundaryType)}`,
                      "_blank"
                    )}
                  >
                    <i className="bi bi-download me-1"></i>
                    Download {boundaryType} Template
                  </button>
                </div>
                <div className="alert alert-info">
                  <h6 className="alert-heading"><i className="bi bi-info-circle me-2"></i>CSV File Format</h6>
                  <p className="mb-2">Your CSV file should have the following columns:</p>
                  <ul className="mb-0">
                    <li><strong>boundary_code</strong> - Unique boundary code</li>
                    <li><strong>boundary_name</strong> - Name of the {boundaryType}</li>
                    <li><strong>boundary_initials</strong> - Initials/abbreviation</li>
                    <li><strong>postcode</strong> - Postcode (optional)</li>
                    <li><strong>boundary_parent_code</strong> - Parent boundary code (e.g., District code for Locality)</li>
                    <li><strong>island_code</strong> - Island code (UNG or PEM)</li>
                    <li><strong>centroid</strong> - Latitude, Longitude together (optional, e.g. "-6.16, 39.20")</li>
                    {isLine ? (
                      <li><strong>boundary_line</strong> - Road/street line as WKT/GeoJSON LineString (optional)</li>
                    ) : (
                      <li><strong>boundary_polygon</strong> - Area outline as WKT/GeoJSON MultiPolygon (optional)</li>
                    )}
                  </ul>
                  <hr />
                  <p className="mb-0 small">
                    <strong>Tip:</strong> Click "Download {boundaryType} Template" above to get a
                    ready-made CSV with the correct columns{isLine ? " (including the line geometry)" : ""}.
                  </p>
                </div>

                <div className="mb-3">
                  <label htmlFor="csvFileBoundary" className="form-label">Select CSV File <span className="text-danger">*</span></label>
                  <input
                    type="file"
                    className="form-control"
                    id="csvFileBoundary"
                    accept=".csv"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                  />
                  {fileName && (
                    <div className="mt-2 text-success">
                      <i className="bi bi-file-earmark-check me-1"></i>
                      Selected: {fileName}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <h6 className="mb-0">
                      <i className="bi bi-eye me-2"></i>
                      Preview: {fileName}
                    </h6>
                    <small className="text-muted">Showing first {totalRows} rows</small>
                  </div>
                  <button className="btn btn-sm btn-outline-secondary" onClick={handleReset}>
                    <i className="bi bi-arrow-left me-1"></i>
                    Select Different File
                  </button>
                </div>

                <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table className="table table-bordered table-striped table-sm">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th>#</th>
                        {headers.map((header, idx) => (
                          <th key={idx}>{header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.map((row, rowIdx) => (
                        <tr key={rowIdx}>
                          <td>{rowIdx + 1}</td>
                          {headers.map((header, colIdx) => (
                            <td key={colIdx}>{row[header] || '-'}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="alert alert-warning mt-3 mb-0">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Please verify the data above before uploading. Duplicate codes will be skipped.
                </div>
              </>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal" onClick={handleReset}>
              Cancel
            </button>
            {showPreview && (
              <button type="button" className="btn btn-primary" onClick={handleUpload} disabled={loading || !file}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className="bi bi-upload me-2"></i>
                    Upload {boundaryType}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
